import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { type Choices, type FilterState, parseState, serializeState, toQuery } from './filters';

/** Filter state lives in the URL: shareable links, back button, no store. */
export function useFilters(choices: Choices | null) {
  const [sp, setSp] = useSearchParams();

  const state = useMemo(() => (choices ? parseState(sp, choices) : null), [sp, choices]);

  const update = useCallback(
    (patch: Partial<FilterState>) => {
      if (!choices || !state) return;
      setSp((prev) => serializeState({ ...state, ...patch }, choices, prev), { replace: true });
    },
    [choices, state, setSp],
  );

  const reset = useCallback(
    () =>
      setSp(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const key of ['indicator', 'from', 'to', 'map_year', 'region', 'income', 'ownership']) {
            next.delete(key);
          }
          return next;
        },
        { replace: true }
      ),
    [setSp]
  );

  const query = useMemo(() => (choices && state ? toQuery(state, choices) : null), [choices, state]);

  return { state, query, update, reset };
}
